import axios, { AxiosError, AxiosResponse } from 'axios';
import Task from '../../../models/Task';
import TaskType from '../../../models/TaskType';
import { interceptor } from '../../../utils/axios.interceptor';
import {
  taskCloneEndPoint,
  taskCloseEndPoint,
  taskEndPoint,
  taskExportExcelEndPoint,
  taskReportsEndPoint,
  taskTypeByUserEndPoint,
  taskUsersAssignedEndPoint,
  taskUsersCreatedEndPoint,
  taskReportsExcelEndPoint,
  geometriesEndPoint,
} from '../../../utils/endPoints';
import { ResultsPage } from '../../../models/ResultsPage';
import { Moment } from 'moment';

export const taskInstance = axios.create();
interceptor(taskInstance);

export class TaskService {
  /**
   * Used to get all the tasks
   * @returns task list
   */
  async getTasks(): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      let result;
      const response = await taskInstance.get(taskEndPoint);
      if (response.status === 200) {
        result = { status: true, data: response.data.results };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to get the task types and assigned users
   * @returns task list
   */

  async getTasksTypeByUser(): Promise<{
    status: boolean;
    data: TaskType[] | unknown;
  }> {
    try {
      let result;
      const response = await taskInstance.get(taskTypeByUserEndPoint);
      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  async getUsersAssignedTo(): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      let result;
      const response = await taskInstance.get(taskUsersAssignedEndPoint);
      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  async getUsersCreatedBy(): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      let result;
      const response = await taskInstance.get(taskUsersCreatedEndPoint);
      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to get all the tasks by filters
   * @returns task list
   */
  async createTask(task: Task): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      let result;
      const createTask: Task = {
        tipotareaId: task.tipotareaId,
        titulo: task.titulo,
        descripcion: task.descripcion,
        fechaDesde: task.fechaDesde,
        fechaHasta: task.fechaHasta,
        observaciones: task.observaciones,
        tareaUsuarioId: task.tareaUsuarioId,
      };
      const response = await taskInstance.post(taskEndPoint, createTask);
      if (response.status === 201) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to update the task by id
   * @returns task object updated
   */
  async updateTask(task: Task): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const updateTask: Task = {
        tipotareaId: task.tipotareaId,
        titulo: task.titulo,
        descripcion: task.descripcion,
        fechaDesde: task.fechaDesde,
        fechaHasta: task.fechaHasta,
        observaciones: task.observaciones,
        tareaUsuarioId: task.tareaUsuarioId,
      };
      const response = await taskInstance.put(
        `${taskEndPoint}/${task.id}`,
        updateTask,
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  async getTask(id: number): Promise<Task> {
    const response = await taskInstance.get(taskEndPoint + '/' + id);
    return response.data;
  }

  /**
   * Used to get tasks in file excel
   * @returns file excel
   */
  async getTasksExportExcel({
    search,
    estado,
    tareaUsuarioEstado,
    usuariosAsignados,
    ordenarPor,
    creadasPor,
    tipoTarea,
    tipoFecha,
    fechaDesde,
    fechaHasta,
  }: TareasFiltros): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    let desde: any = fechaDesde?.set({ hours: 0, minute: 0 }).format();
    let hasta: any = fechaHasta ? fechaHasta : fechaDesde;
    hasta = hasta?.set({ hours: 23, minute: 59 })?.format();

    try {
      let result;
      const response = await taskInstance.get(
        `${taskExportExcelEndPoint}?page&limit` +
          (search ? `&search=${search ?? ''}` : '') +
          (estado?.length ? `&estado=${estado.join(',')}` : '') +
          (tareaUsuarioEstado?.length
            ? `&tareaUsuario.estado=${tareaUsuarioEstado.join(',')}`
            : '') +
          (usuariosAsignados?.length
            ? `&tareaUsuario.usuarioId=${usuariosAsignados.join(',')}`
            : '') +
          (ordenarPor ? `&sort=${ordenarPor ?? ''}` : '') +
          (creadasPor?.length ? `&usuario.id=${creadasPor.join(',')}` : '') +
          (tipoTarea?.length ? `&tipotarea.id=${tipoTarea.join(',')}` : '') +
          (tipoFecha === 'createdAt' && desde && hasta
            ? `&createdAt=range(${[desde, hasta].join(',')})`
            : '') +
          (tipoFecha === 'fechaRealizacion' && desde && hasta
            ? `&fechaRealizacion=${[desde, hasta].join(',')}`
            : '') +
          (tipoFecha === 'closedAt' && desde && hasta
            ? `&closedAt=range(${[desde, hasta].join(',')})`
            : ''),
        {
          responseType: 'blob',
        },
      );
      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to delete the task by id
   * @returns task object deleted
   */
  async deleteTask(taskId: number): Promise<{
    status: boolean;
    data: AxiosResponse | unknown;
  }> {
    try {
      const response = await taskInstance.delete(`${taskEndPoint}/${taskId}`);
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to clone the task by id
   * @returns task object clone
   */
  async cloneTask(taskId: number): Promise<{
    status: boolean;
    data: Task | unknown;
  }> {
    try {
      let result;
      const response = await taskInstance.get(`${taskCloneEndPoint}/${taskId}`);

      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  /**
   * Used to close the task by id
   * @returns task object close
   */
  async closeTask(
    taskId: number,
    noteTask: string,
  ): Promise<{
    status: boolean;
    data: Task | unknown;
  }> {
    try {
      let result;
      let closeNote = {
        notaDeCierre: noteTask,
      };
      const response = await taskInstance.put(
        `${taskCloseEndPoint}/${taskId}`,
        closeNote,
      );

      if (response.status === 200) {
        result = { status: true, data: response.data };
      } else {
        result = { status: false, data: [] };
      }
      return result;
    } catch (error) {
      const err = error as AxiosError;
      return {
        status: false,
        data: err.response?.data,
      };
    }
  }

  static getReportTasks = async ({
    page,
    pageSize,
    search,
    expediente,
    tipoTarea,
    categorias,
    fechaDesde,
    fechaHasta,
    tipoFecha,
  }: ReportesFiltros): Promise<ResultsPage<Task>> => {
    let desde: any = fechaDesde?.set({ hours: 0, minute: 0 }).format();
    let hasta: any = fechaHasta ? fechaHasta : fechaDesde;
    hasta = hasta?.set({ hours: 23, minute: 59 })?.format();

    const res = await taskInstance.get(
      `${taskReportsEndPoint}?page=${Math.max(page, 1)}` +
        `&limit=${pageSize}` +
        (search ? `&search=${search ?? ''}` : '') +
        (expediente ? `&expediente=${expediente ?? ''}` : '') +
        (tipoTarea.length ? `&tipotarea.id=${tipoTarea.join(',')}` : '') +
        (categorias.length
          ? `&geometrias.categoria=${categorias.join(',')}`
          : '') +
        (tipoFecha === 'cierre' && desde && hasta
          ? `&closedAt=range(${[desde, hasta].join(',')})`
          : '') +
        (tipoFecha === 'realizacion' && desde && hasta
          ? `&fechaRealizacion=${[desde, hasta].join(',')}`
          : ''),
    );
    return res.data;
  };

  /**
   * Used to get all the tasks by filters
   * @returns task list
   */
  static getAllTasksFilters = async ({
    page,
    pageSize,
    search,
    estado,
    tareaUsuarioEstado,
    usuariosAsignados,
    ordenarPor,
    creadasPor,
    tipoTarea,
    tipoFecha,
    fechaDesde,
    fechaHasta,
    cancelToken,
  }: TareasFiltros): Promise<ResultsPage<Task>> => {
    let desde: any = fechaDesde?.set({ hours: 0, minute: 0 }).format();
    let hasta: any = fechaHasta ? fechaHasta : fechaDesde;
    hasta = hasta?.set({ hours: 23, minute: 59 })?.format();

    const options = { cancelToken: cancelToken.token };

    const res = await taskInstance.get(
      `${taskEndPoint}?page=${Math.max(page, 1)}` +
        `&limit=${pageSize}` +
        (search ? `&search=${search ?? ''}` : '') +
        (estado?.length ? `&estado=${estado.join(',')}` : '') +
        (tareaUsuarioEstado?.length
          ? `&tareaUsuario.estado=${tareaUsuarioEstado.join(',')}`
          : '') +
        (usuariosAsignados?.length
          ? `&tareaUsuario.usuarioId=${usuariosAsignados.join(',')}`
          : '') +
        (ordenarPor ? `&sort=${ordenarPor ?? ''}` : '') +
        (creadasPor?.length ? `&usuario.id=${creadasPor.join(',')}` : '') +
        (tipoTarea?.length ? `&tipotarea.id=${tipoTarea.join(',')}` : '') +
        (tipoFecha === 'createdAt' && desde && hasta
          ? `&createdAt=range(${[desde, hasta].join(',')})`
          : '') +
        (tipoFecha === 'fechaRealizacion' && desde && hasta
          ? `&fechaRealizacion=${[desde, hasta].join(',')}`
          : '') +
        (tipoFecha === 'closedAt' && desde && hasta
          ? `&closedAt=range(${[desde, hasta].join(',')})`
          : ''),
      options
    );
    return res.data;
  };

  /**
   * Used to get the points of geometry in file excel
   * @returns file excel
   */
  static async getReportsExcel(idsPoints: number[]): Promise<any> {
    const response = await taskInstance.post(
      taskReportsExcelEndPoint,
      { ids: idsPoints },
      {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return response.data;
  }

  static downloadWordReport = async (points: number[]): Promise<any> => {
    const res = await taskInstance.post(
      geometriesEndPoint + '/informes_word',
      {
        puntosId: points,
      },
      {
        responseType: 'blob',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    return res.data;
  };

  static downloadKmzFile = async (id: number): Promise<any> => {
    const res = await taskInstance.get(taskEndPoint + '/exportar/' + id, {
      responseType: 'blob',
    });
    return res.data;
  };
}

export type ReportesFiltros = {
  page: number;
  pageSize: number;
  search?: string;
  expediente?: string;
  fechaDesde?: Moment;
  fechaHasta?: Moment;
  rangoFechas?: boolean;
  tipoFecha: 'realizacion' | 'cierre';
  categorias: number[];
  tipoTarea: number[];
};

export type TareasFiltros = {
  page: number;
  pageSize: number;
  search?: string;
  estado?: number[];
  tareaUsuarioEstado?: number[];
  usuariosAsignados?: string[];
  ordenarPor?: string;
  creadasPor?: string[];
  tipoTarea?: any[];
  tipoFecha?: 'createdAt' | 'fechaRealizacion' | 'closedAt';
  fechaDesde?: Moment;
  fechaHasta?: Moment;
  rangoFechas?: boolean;
  cancelToken?: any;
};
